// http-error.interceptor.ts
// Interceptor centralizado para exibir toasts com as mensagens detalhadas
// retornadas pelo backend em respostas de erro 4xx/5xx.
//
// Formato esperado do backend:
//   { status, error, message, errors?: [{ campo, mensagem }], path }
//
// Regras de silenciamento:
//   - STATUS_SILENCIOSOS (401, 403, 404): silenciados para TODOS os métodos
//   - URLS_SILENCIOSAS: silenciadas para todos os métodos
//   - GETs com 4xx/5xx: silenciados — componentes tratam erros de listagem localmente
//   - GETs com status 0 (rede offline, CORS, timeout): exibem toast global
//   - Mutacoes (POST/PUT/PATCH/DELETE): toast para qualquer erro nao-silenciado
//
// O interceptor re-lança o erro após exibir o toast para que os handlers
// locais (catchError nas páginas) possam continuar sua lógica própria
// (ex.: resetar loading spinner).

import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

// URLs de endpoints que devem falhar silenciosamente (sem toast)
const URLS_SILENCIOSAS = ['/login', '/nfe/consultar', '/nfe/transmitir', '/verifica-certificado-digital-cadastrado', '/unidaDeMedidaDeItemEmpresa/unidadeMedidaPadrao', '/indicacoes/convite', '/repasse'];

// Status HTTP que devem ser silenciados globalmente:
// 401 - tratado pelo AuthInterceptor (redirect para login)
// 403 - tratado pelo AuthInterceptor ou guards
// 404 - "nao encontrado" eh estado normal, nao erro (lista vazia, recurso inexistente)
const STATUS_SILENCIOSOS = [401, 403, 404];

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private messageService: MessageService,
    private translate: TranslateService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Processar erros HTTP 4xx/5xx e erros de rede (status 0 = sem conexao/timeout)
        const ehErroHttp = error instanceof HttpErrorResponse && (error.status >= 400 || error.status === 0);
        if (ehErroHttp) {
          // Silenciar status que nao sao erros reais de negocio
          const ehStatusSilencioso = STATUS_SILENCIOSOS.includes(error.status);

          // Verificar se e uma URL que deve falhar silenciosamente
          const ehUrlSilenciosa = URLS_SILENCIOSAS.some(url => req.url.includes(url));

          // Em GETs: silenciar 4xx e 5xx — componentes de listagem tratam erros localmente.
          // Apenas status 0 (rede offline, CORS, timeout) dispara toast global em GETs.
          // Em mutacoes (POST/PUT/PATCH/DELETE): toast para qualquer erro nao-silenciado.
          const ehConsultaSilenciosa = req.method === 'GET' && error.status !== 0;

          if (!ehStatusSilencioso && !ehUrlSilenciosa && !ehConsultaSilenciosa) {
            this.exibirToastDeErro(error);
          }
        }
        return throwError(() => error);
      })
    );
  }

  private exibirToastDeErro(error: HttpErrorResponse): void {
    // Quando o endpoint usa responseType: 'text' (ex: DELETE), o Angular entrega
    // error.error como string mesmo que o backend tenha retornado JSON.
    // Tentamos parsear para extrair a mensagem estruturada do backend.
    let corpo = error.error;
    if (typeof corpo === 'string') {
      try {
        corpo = JSON.parse(corpo);
      } catch {
        // Corpo não é JSON — manter como string simples
      }
    }

    // Extrair mensagem principal preferindo o campo 'message' do backend.
    // Para erros 5xx sem mensagem estruturada, exibir texto genérico.
    const mensagemBackend: string | undefined =
      typeof corpo === 'object' ? corpo?.message : corpo || undefined;

    const summary: string =
      mensagemBackend ||
      (error.status >= 500
        ? this.translate.instant('common.erro_servidor')
        : this.translate.instant('common.erro_servidor'));

    // Extrair detalhes de validação (errors[])
    let detail = '';
    if (typeof corpo === 'object' && corpo?.errors && Array.isArray(corpo.errors) && corpo.errors.length > 0) {
      detail = corpo.errors
        .map((e: { campo?: string; mensagem?: string }) =>
          e.campo ? `${e.campo}: ${e.mensagem}` : (e.mensagem ?? '')
        )
        .join('\n');
    }

    this.messageService.add({
      severity: 'error',
      summary,
      detail,
      life: 6000,
    });
  }
}
